import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { stlAPI, imageAPI } from '../lib/api';
import {
  FolderOpen,
  Layers,
  Wrench,
  Search as SearchIcon,
  Eye,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Image,
  ChevronRight,
  Filter,
  Calendar,
  AlertTriangle,
} from 'lucide-react';

const MyProjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const [supportRes, stlRes, imageRes] = await Promise.allSettled([
        api.get('/supports/history?limit=50'),
        stlAPI.getHistory(50),
        imageAPI.getHistory(50),
      ]);

      const supportData = supportRes.status === 'fulfilled' ? supportRes.value.data : [];
      const stlData = stlRes.status === 'fulfilled' ? stlRes.value.data : [];
      const imageData = imageRes.status === 'fulfilled' ? imageRes.value.data : [];

      // Build unified project list by grouping by filename
      const projectMap = new Map();

      // Support generations
      supportData.forEach((item) => {
        const key = item.original_filename || item.id;
        if (!projectMap.has(key)) {
          projectMap.set(key, {
            id: item.id,
            filename: item.original_filename || 'Unknown',
            created_at: item.created_at,
            type: 'stl',
            analysis: null,
            repair: null,
            support: item,
          });
        } else {
          projectMap.get(key).support = item;
        }
      });

      // STL analyses
      stlData.forEach((item) => {
        const key = item.filename || item.id;
        if (!projectMap.has(key)) {
          projectMap.set(key, {
            id: item.id,
            filename: item.filename || 'Unknown',
            created_at: item.created_at,
            type: 'stl',
            analysis: item,
            repair: null,
            support: null,
          });
        } else {
          const proj = projectMap.get(key);
          proj.analysis = item;
          if (!proj.created_at || new Date(item.created_at) < new Date(proj.created_at)) {
            proj.created_at = item.created_at;
          }
        }
      });

      // Image diagnoses
      imageData.forEach((item) => {
        const key = `img_${item.id}`;
        projectMap.set(key, {
          id: item.id,
          filename: item.filename || `Diagnosis ${item.id?.slice(0, 8)}`,
          created_at: item.created_at,
          type: 'image',
          analysis: item,
          repair: null,
          support: null,
        });
      });

      // Sort by most recent
      const sorted = Array.from(projectMap.values()).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setProjects(sorted);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProjectStatus = (project) => {
    if (project.support?.status === 'completed') return { label: 'Supports Ready', color: 'text-green-400', bg: 'bg-green-500/15' };
    if (project.support?.status === 'processing') return { label: 'Processing', color: 'text-yellow-400', bg: 'bg-yellow-500/15' };
    if (project.support?.status === 'failed') return { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/15' };
    if (project.repair) return { label: 'Repaired', color: 'text-blue-400', bg: 'bg-blue-500/15' };
    if (project.analysis) return { label: 'Analyzed', color: 'text-purple-400', bg: 'bg-purple-500/15' };
    return { label: 'Queued', color: 'text-text-muted', bg: 'bg-text-muted/15' };
  };

  const getStepStatus = (project) => {
    const steps = [];
    
    if (project.type === 'image') {
      steps.push({
        label: 'Diagnosis',
        done: !!project.analysis,
        icon: Eye,
      });
      return steps;
    }

    steps.push({
      label: 'Analysis',
      done: !!project.analysis,
      icon: SearchIcon,
    });
    steps.push({
      label: 'Repair',
      done: !!project.repair,
      icon: Wrench,
    });
    steps.push({
      label: 'Supports',
      done: project.support?.status === 'completed',
      icon: Layers,
      processing: project.support?.status === 'processing',
      failed: project.support?.status === 'failed',
    });

    return steps;
  };

  const filtered = projects.filter((p) => {
    if (filter === 'stl' && p.type !== 'stl') return false;
    if (filter === 'image' && p.type !== 'image') return false;
    if (filter === 'completed' && p.support?.status !== 'completed') return false;
    if (searchQuery && !p.filename.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="my-projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-primary/15 rounded-xl">
            <FolderOpen className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">My Projects</h1>
            <p className="text-text-secondary text-sm">All your uploads and processing history</p>
          </div>
        </div>
        <button
          onClick={fetchProjects}
          className="flex items-center gap-2 px-4 py-2 bg-bg-light border border-text-muted/20 text-text-primary rounded-lg hover:border-text-muted/40 transition"
          data-testid="refresh-projects-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-bg-light border border-text-muted/20 rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary/50"
            data-testid="project-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-text-muted" />
          {['all', 'stl', 'image', 'completed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                filter === f
                  ? 'bg-primary text-white'
                  : 'bg-bg-light text-text-secondary hover:text-text-primary border border-text-muted/20'
              }`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : f === 'stl' ? 'STL' : f === 'image' ? 'Images' : 'Completed'}
            </button>
          ))}
        </div>
      </div>

      {/* Projects List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-bg-light rounded-xl border border-text-muted/20">
          <FolderOpen className="w-14 h-14 text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Projects Yet</h3>
          <p className="text-text-secondary mb-6 max-w-md mx-auto">
            Upload an STL file or an image to start your first project
          </p>
          <button
            onClick={() => navigate('/dashboard/smart-support')}
            className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg transition"
            data-testid="start-project-btn"
          >
            Start New Project
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((project) => {
            const status = getProjectStatus(project);
            const steps = getStepStatus(project);
            const isExpanded = expandedId === project.id;

            return (
              <div
                key={project.id}
                className={`bg-bg-light rounded-xl border transition-all ${
                  isExpanded ? 'border-primary/40' : 'border-text-muted/20 hover:border-text-muted/30'
                }`}
                data-testid={`project-card-${project.id}`}
              >
                {/* Main Row */}
                <button
                  className="w-full p-4 flex items-center gap-4 text-left"
                  onClick={() => setExpandedId(isExpanded ? null : project.id)}
                >
                  {/* File Icon */}
                  <div className={`p-2 rounded-lg ${project.type === 'image' ? 'bg-orange-500/15' : 'bg-primary/15'}`}>
                    {project.type === 'image' ? (
                      <Image className="w-5 h-5 text-orange-400" />
                    ) : (
                      <FileText className="w-5 h-5 text-primary" />
                    )}
                  </div>

                  {/* Name & Date */}
                  <div className="flex-1 min-w-0">
                    <p className="text-text-primary font-medium truncate">{project.filename}</p>
                    <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
                      <Calendar className="w-3 h-3" />
                      {formatDate(project.created_at)}
                    </div>
                  </div>

                  {/* Pipeline Steps (desktop) */}
                  <div className="hidden md:flex items-center gap-1">
                    {steps.map((step, i) => {
                      const StepIcon = step.icon;
                      return (
                        <React.Fragment key={step.label}>
                          <div
                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                              step.done
                                ? 'bg-green-500/15 text-green-400'
                                : step.processing
                                ? 'bg-yellow-500/15 text-yellow-400'
                                : step.failed
                                ? 'bg-red-500/15 text-red-400'
                                : 'bg-text-muted/10 text-text-muted'
                            }`}
                          >
                            <StepIcon className="w-3 h-3" />
                            {step.label}
                          </div>
                          {i < steps.length - 1 && (
                            <ChevronRight className="w-3 h-3 text-text-muted/50" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Status Badge */}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                    {status.label}
                  </span>

                  {/* Chevron */}
                  <ChevronRight className={`w-4 h-4 text-text-muted transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-text-muted/10 pt-4">
                    {/* Pipeline Steps (mobile) */}
                    <div className="flex md:hidden items-center gap-1 mb-4 flex-wrap">
                      {steps.map((step, i) => {
                        const StepIcon = step.icon;
                        return (
                          <React.Fragment key={step.label}>
                            <div
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                step.done
                                  ? 'bg-green-500/15 text-green-400'
                                  : step.processing
                                  ? 'bg-yellow-500/15 text-yellow-400'
                                  : step.failed
                                  ? 'bg-red-500/15 text-red-400'
                                  : 'bg-text-muted/10 text-text-muted'
                              }`}
                            >
                              <StepIcon className="w-3 h-3" />
                              {step.label}
                            </div>
                            {i < steps.length - 1 && (
                              <ChevronRight className="w-3 h-3 text-text-muted/50" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Analysis Card */}
                      {project.type === 'stl' && (
                        <div className={`p-3 rounded-lg border ${project.analysis ? 'bg-purple-500/5 border-purple-500/20' : 'bg-bg-dark/30 border-text-muted/10'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <SearchIcon className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-medium text-text-primary">Analysis</span>
                            {project.analysis ? <CheckCircle className="w-3.5 h-3.5 text-green-400 ml-auto" /> : <Clock className="w-3.5 h-3.5 text-text-muted ml-auto" />}
                          </div>
                          {project.analysis ? (
                            <p className="text-xs text-text-secondary">
                              {project.analysis.geometry?.faces || '?'} faces, {project.analysis.geometry?.vertices || '?'} vertices
                            </p>
                          ) : (
                            <p className="text-xs text-text-muted">Not yet analyzed</p>
                          )}
                        </div>
                      )}

                      {/* Repair Card */}
                      {project.type === 'stl' && (
                        <div className={`p-3 rounded-lg border ${project.repair ? 'bg-blue-500/5 border-blue-500/20' : 'bg-bg-dark/30 border-text-muted/10'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Wrench className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-medium text-text-primary">Repair</span>
                            {project.repair ? <CheckCircle className="w-3.5 h-3.5 text-green-400 ml-auto" /> : <Clock className="w-3.5 h-3.5 text-text-muted ml-auto" />}
                          </div>
                          {project.repair ? (
                            <p className="text-xs text-text-secondary">Repaired successfully</p>
                          ) : (
                            <p className="text-xs text-text-muted">Not yet repaired</p>
                          )}
                        </div>
                      )}

                      {/* Support Card */}
                      {project.type === 'stl' && (
                        <div className={`p-3 rounded-lg border ${
                          project.support?.status === 'completed'
                            ? 'bg-green-500/5 border-green-500/20'
                            : project.support?.status === 'processing'
                            ? 'bg-yellow-500/5 border-yellow-500/20'
                            : project.support?.status === 'failed'
                            ? 'bg-red-500/5 border-red-500/20'
                            : 'bg-bg-dark/30 border-text-muted/10'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Layers className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-medium text-text-primary">Supports</span>
                            {project.support?.status === 'completed' ? (
                              <CheckCircle className="w-3.5 h-3.5 text-green-400 ml-auto" />
                            ) : project.support?.status === 'processing' ? (
                              <RefreshCw className="w-3.5 h-3.5 text-yellow-400 animate-spin ml-auto" />
                            ) : project.support?.status === 'failed' ? (
                              <XCircle className="w-3.5 h-3.5 text-red-400 ml-auto" />
                            ) : (
                              <Clock className="w-3.5 h-3.5 text-text-muted ml-auto" />
                            )}
                          </div>
                          {project.support?.status === 'completed' ? (
                            <div className="text-xs text-text-secondary">
                              <p>{project.support.tree_style} mode, {project.support.processing_time?.toFixed(1)}s</p>
                            </div>
                          ) : project.support?.status === 'failed' ? (
                            <p className="text-xs text-red-400">{project.support.error_message?.slice(0, 60) || 'Generation failed'}</p>
                          ) : (
                            <p className="text-xs text-text-muted">Not yet generated</p>
                          )}
                        </div>
                      )}

                      {/* Image Diagnosis Card */}
                      {project.type === 'image' && (
                        <div className="p-3 rounded-lg border bg-orange-500/5 border-orange-500/20 col-span-full sm:col-span-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Eye className="w-4 h-4 text-orange-400" />
                            <span className="text-sm font-medium text-text-primary">Diagnosis</span>
                            <CheckCircle className="w-3.5 h-3.5 text-green-400 ml-auto" />
                          </div>
                          <p className="text-xs text-text-secondary">
                            {project.analysis?.issues_found || 0} issues found
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-text-muted/10">
                      {project.type === 'stl' && (
                        <>
                          <button
                            onClick={() => navigate('/dashboard/smart-support')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/15 hover:bg-primary/25 text-primary rounded-lg text-sm transition"
                            data-testid={`project-generate-${project.id}`}
                          >
                            <Layers className="w-3.5 h-3.5" />
                            Generate Supports
                          </button>
                          {project.support?.status === 'completed' && project.support?.stl_with_supports_id && (
                            <button
                              onClick={() => {
                                const url = `${process.env.REACT_APP_BACKEND_URL}/api/supports/${project.support.id}/download/stl?token=${localStorage.getItem('token')}`;
                                window.open(url, '_blank');
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/15 hover:bg-accent/25 text-accent rounded-lg text-sm transition"
                              data-testid={`project-download-${project.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download STL
                            </button>
                          )}
                        </>
                      )}
                      {project.type === 'image' && (
                        <button
                          onClick={() => navigate('/dashboard/diagnosis')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/15 hover:bg-orange-500/25 text-orange-400 rounded-lg text-sm transition"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          New Diagnosis
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyProjects;
